import type {
  AudiencePreviewQuery,
  CommunicationCreateRequest,
  CommunicationListQuery,
  CouncilConfirmRequest,
  CouncilLinkRequest,
  CouncilListResponse,
  CouncilResponse,
  CouncilSetupRequest,
  CouncilUpdateRequest,
  ElectionCreateRequest,
  ElectionResponse,
  ElectionStatus,
  ElectionUpdateRequest,
  EndElectionRequest,
  ExtendElectionRequest,
  GeoBoundaryQuery,
  GeoSearchQuery,
  MemberDetailResponse,
  MemberListQuery,
  MemberMapQuery,
  OrganizationCreateRequest,
  OrganizationResponse,
  OrganizationUpdateRequest,
  OrgSignupRequest,
  ReadmitMemberRequest,
  RecipientListQuery,
  RemoveMemberRequest,
  ResetMemberRequest,
  RequirementsChangePreviewRequest,
  ReviewQueueQuery,
  ReviewRequest,
  SetDistrictRequest,
  TestSendRequest,
} from '@de/shared'
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from './client'
import { queryKeys } from './keys'

/*
 * Organizer-side TanStack Query hooks for EVERY organizer route (organizations, members, votings, communications).
 * SHARED, FROZEN after Phase 0 (B1 consumes them; changes are requested through the handoff).
 * Pattern: one hook per route, keys from api/keys.ts, mutations refresh the affected caches.
 * Vocabulary: a "voting" in the UI is an `election` in the API.
 */

/** Voting statuses during which the backend is doing work the organizer is waiting for ⇒ poll. */
export const BUSY_ELECTION_STATUSES: readonly ElectionStatus[] = ['starting', 'closing', 'ended']

const pollWhileBusy = (status: ElectionStatus | undefined, ms = 4000) =>
  status && BUSY_ELECTION_STATUSES.includes(status) ? ms : false

/* ================================================================== auth */

export const useOrgLogin = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api('orgLogin', { body }),
    onSuccess: (data) => {
      qc.removeQueries({ queryKey: ['org'] })
      qc.setQueryData(queryKeys.org.me, data)
    },
  })
}

export const useOrgSignup = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OrgSignupRequest) => api('orgSignup', { body }),
    onSuccess: (data) => {
      qc.removeQueries({ queryKey: ['org'] })
      qc.setQueryData(queryKeys.org.me, data)
    },
  })
}

/* ================================================================== organizations */

export const useOrganizations = () =>
  useQuery({ queryKey: queryKeys.org.organizations, queryFn: () => api('listOrganizations') })

export const useOrganization = (orgId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.organization(orgId ?? ''),
    queryFn: () => api('getOrganization', { params: { orgId: orgId! } }),
    enabled: Boolean(orgId),
  })

/** Wizard hint only (the create / PATCH re-check and answer 409 slug_taken). */
export const useSlugAvailability = (slug: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.slugAvailable(slug ?? ''),
    queryFn: () => api('checkOrganizationSlug', { query: { slug: slug! } }),
    enabled: Boolean(slug),
    staleTime: 5_000,
  })

/** Store a fresh organization everywhere it is cached and refresh the derived views. */
export const storeOrganization = (qc: QueryClient, data: OrganizationResponse) => {
  qc.setQueryData(queryKeys.org.organization(data.organization.id), data)
  void qc.invalidateQueries({ queryKey: queryKeys.org.organizations, exact: true })
  void qc.invalidateQueries({ queryKey: queryKeys.org.dashboard(data.organization.id) })
}

export const useCreateOrganization = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OrganizationCreateRequest) => api('createOrganization', { body }),
    onSuccess: (data) => storeOrganization(qc, data),
  })
}

export const useUpdateOrganization = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OrganizationUpdateRequest) => api('updateOrganization', { params: { orgId }, body }),
    onSuccess: (data) => storeOrganization(qc, data),
  })
}

export const usePublishOrganization = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api('publishOrganization', { params: { orgId } }),
    onSuccess: (data) => storeOrganization(qc, data),
  })
}

export const useDeleteOrganization = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orgId: string) => api('deleteOrganization', { params: { orgId } }),
    onSuccess: (_, orgId) => {
      qc.removeQueries({ queryKey: queryKeys.org.organization(orgId) })
      void qc.invalidateQueries({ queryKey: queryKeys.org.organizations, exact: true })
    },
  })
}

/** Overview tab KPIs; refreshed every 15 s while the tab is open. */
export const useOrganizationDashboard = (orgId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.dashboard(orgId ?? ''),
    queryFn: () => api('organizationDashboard', { params: { orgId: orgId! } }),
    enabled: Boolean(orgId),
    refetchInterval: 15_000,
  })

export const useMemberMap = (orgId: string, query: MemberMapQuery = {}) =>
  useQuery({
    queryKey: queryKeys.org.map(orgId, query as Record<string, unknown>),
    queryFn: () => api('memberMap', { params: { orgId }, query }),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  })

/* ================================================================== members */

export const useMembers = (orgId: string, query: MemberListQuery) =>
  useQuery({
    queryKey: queryKeys.org.members(orgId, query as Record<string, unknown>),
    queryFn: () => api('listMembers', { params: { orgId }, query }),
    placeholderData: keepPreviousData,
    refetchInterval: 20_000,
  })

export const useReviewQueue = (orgId: string, query: ReviewQueueQuery = {}) =>
  useQuery({
    queryKey: queryKeys.org.reviewQueue(orgId, query as Record<string, unknown>),
    queryFn: () => api('reviewQueue', { params: { orgId }, query }),
    placeholderData: keepPreviousData,
    refetchInterval: 20_000,
  })

export const useMember = (orgId: string, memberId: number) =>
  useQuery({
    queryKey: queryKeys.org.member(orgId, memberId),
    queryFn: () => api('getMember', { params: { orgId, memberId } }),
    enabled: Boolean(orgId) && Number.isFinite(memberId),
  })

/** Live Didit decision (M6): fresh presigned image URLs, never cached for long. */
export const useLiveDecision = (orgId: string, memberId: number, sessionId: string | null) =>
  useQuery({
    queryKey: queryKeys.org.liveDecision(orgId, memberId, sessionId ?? ''),
    queryFn: () => api('liveDecision', { params: { orgId, memberId, sessionId: sessionId! } }),
    enabled: Boolean(sessionId),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })

const useMemberMutation = <B>(orgId: string, memberId: number, fn: (body: B) => Promise<MemberDetailResponse>) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => qc.setQueryData(queryKeys.org.member(orgId, memberId), data),
    // Success or refusal (e.g. 409: the member changed meanwhile): every view of the member is refreshed — detail
    // and lists (membersRoot), review queue, status counts (dashboard, organizations list) and the members map.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.org.membersRoot(orgId) })
      void qc.invalidateQueries({ queryKey: [...queryKeys.org.organization(orgId), 'review-queue'] })
      void qc.invalidateQueries({ queryKey: queryKeys.org.dashboard(orgId) })
      void qc.invalidateQueries({ queryKey: [...queryKeys.org.organization(orgId), 'map'] })
      void qc.invalidateQueries({ queryKey: queryKeys.org.organizations, exact: true })
    },
  })
}

export const useReviewMember = (orgId: string, memberId: number) =>
  useMemberMutation(orgId, memberId, (body: ReviewRequest) =>
    api('reviewMember', { params: { orgId, memberId }, body })
  )

export const useSetMemberDistrict = (orgId: string, memberId: number) =>
  useMemberMutation(orgId, memberId, (body: SetDistrictRequest) =>
    api('setMemberDistrict', { params: { orgId, memberId }, body })
  )

export const useRemoveMember = (orgId: string, memberId: number) =>
  useMemberMutation(orgId, memberId, (body: RemoveMemberRequest) =>
    api('removeMember', { params: { orgId, memberId }, body })
  )

export const useReadmitMember = (orgId: string, memberId: number) =>
  useMemberMutation(orgId, memberId, (body: ReadmitMemberRequest) =>
    api('readmitMember', { params: { orgId, memberId }, body })
  )

export const useResetMember = (orgId: string, memberId: number) =>
  useMemberMutation(orgId, memberId, (body: ResetMemberRequest) =>
    api('resetMember', { params: { orgId, memberId }, body })
  )

/* ================================================================== votings ("elections") */

export const useElections = (orgId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.elections(orgId ?? ''),
    queryFn: () => api('listElections', { params: { orgId: orgId! } }),
    enabled: Boolean(orgId),
  })

export const useElection = (orgId: string | undefined, electionId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.election(orgId ?? '', electionId ?? ''),
    queryFn: () => api('getElection', { params: { orgId: orgId!, electionId: electionId! } }),
    enabled: Boolean(orgId && electionId),
    refetchInterval: (q) => pollWhileBusy(q.state.data?.election.status),
  })

/** Store a fresh voting everywhere it is cached and refresh the derived views. */
export const storeElection = (qc: QueryClient, data: ElectionResponse) => {
  const { organizationId: orgId, id } = data.election
  qc.setQueryData(queryKeys.org.election(orgId, id), data)
  void qc.invalidateQueries({ queryKey: queryKeys.org.elections(orgId), exact: true })
  void qc.invalidateQueries({ queryKey: queryKeys.org.electionDashboard(orgId, id) })
  void qc.invalidateQueries({ queryKey: queryKeys.org.dashboard(orgId) })
}

export const useCreateElection = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ElectionCreateRequest) => api('createElection', { params: { orgId }, body }),
    onSuccess: (data) => storeElection(qc, data),
  })
}

export const useUpdateElection = (orgId: string, electionId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ElectionUpdateRequest) => api('updateElection', { params: { orgId, electionId }, body }),
    onSuccess: (data) => storeElection(qc, data),
  })
}

export const useDeleteElection = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (electionId: string) => api('deleteElection', { params: { orgId, electionId } }),
    onSuccess: (_, electionId) => {
      qc.removeQueries({ queryKey: queryKeys.org.election(orgId, electionId) })
      void qc.invalidateQueries({ queryKey: queryKeys.org.elections(orgId), exact: true })
    },
  })
}

export type LifecycleAction =
  | { kind: 'publish' }
  | { kind: 'start' }
  | { kind: 'retry' }
  | { kind: 'cancel' }
  | { kind: 'end'; body: EndElectionRequest }
  | { kind: 'extend'; body: ExtendElectionRequest }

export const runLifecycleAction = (
  orgId: string,
  electionId: string,
  action: LifecycleAction
): Promise<ElectionResponse> => {
  const params = { orgId, electionId }
  switch (action.kind) {
    case 'publish':
      return api('publishElection', { params })
    case 'start':
      return api('startElection', { params })
    case 'retry':
      return api('retryStartElection', { params })
    case 'cancel':
      return api('cancelElection', { params })
    case 'end':
      return api('endElection', { params, body: action.body })
    case 'extend':
      return api('extendElection', { params, body: action.body })
  }
}

export const useLifecycleAction = (orgId: string, electionId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (action: LifecycleAction) => runLifecycleAction(orgId, electionId, action),
    onSuccess: (data) => storeElection(qc, data),
  })
}

export const useElectionDashboard = (orgId: string | undefined, electionId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.electionDashboard(orgId ?? '', electionId ?? ''),
    queryFn: () => api('electionDashboard', { params: { orgId: orgId!, electionId: electionId! } }),
    enabled: Boolean(orgId && electionId),
    // KPIs move while voting is open: refresh every 15 s, faster while a lifecycle job runs.
    refetchInterval: (q) => pollWhileBusy(q.state.data?.election.status, 3000) || 15_000,
  })

export const useElectionResults = (
  orgId: string | undefined,
  electionId: string | undefined,
  status?: ElectionStatus
) =>
  useQuery({
    queryKey: queryKeys.org.electionResults(orgId ?? '', electionId ?? ''),
    queryFn: () => api('electionResults', { params: { orgId: orgId!, electionId: electionId! } }),
    enabled: Boolean(orgId && electionId),
    refetchInterval: status === 'ended' || status === 'closing' ? 15_000 : false,
  })

/* ================================================================== communications */

export const useCommunications = (orgId: string, query: CommunicationListQuery = {}) =>
  useQuery({
    queryKey: queryKeys.org.communications(orgId, query as Record<string, unknown>),
    queryFn: () => api('listCommunications', { params: { orgId }, query }),
    placeholderData: keepPreviousData,
    // Delivery counts move while the email worker sends.
    refetchInterval: (q) =>
      q.state.data?.items.some((c) => c.status === 'queued' || c.status === 'sending') ? 5_000 : false,
  })

export const useCommunication = (orgId: string, communicationId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.org.communication(orgId, communicationId ?? ''),
    queryFn: () => api('getCommunication', { params: { orgId, communicationId: communicationId! } }),
    enabled: Boolean(communicationId),
    refetchInterval: (q) => {
      const s = q.state.data?.communication.status
      return s === 'queued' || s === 'sending' ? 5_000 : false
    },
  })

export const useRecipients = (orgId: string, communicationId: string, query: RecipientListQuery = {}) =>
  useQuery({
    queryKey: queryKeys.org.recipients(orgId, communicationId, query as Record<string, unknown>),
    queryFn: () => api('listRecipients', { params: { orgId, communicationId }, query }),
    enabled: Boolean(communicationId),
    placeholderData: keepPreviousData,
  })

export const useAudiencePreview = (orgId: string, query: AudiencePreviewQuery) =>
  useQuery({
    queryKey: queryKeys.org.audience(orgId, query as Record<string, unknown>),
    queryFn: () => api('previewAudience', { params: { orgId }, query }),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  })

export const useCreateCommunication = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CommunicationCreateRequest) => api('createCommunication', { params: { orgId }, body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.org.communicationsRoot(orgId) }),
  })
}

/** "Send a test to me": delivered synchronously to the organizer's own email. */
export const useSendTestCommunication = (orgId: string) =>
  useMutation({ mutationFn: (body: TestSendRequest) => api('sendTestCommunication', { params: { orgId }, body }) })

/* ================================================================== election committees (councils) */

/**
 * The organization's election committees. Forming committees change on chain while their members join and deal, so
 * the list is refreshed every 30 s while one is still forming (the server re-reads the chain on every sync tick).
 */
export const useCouncils = (orgId: string | undefined, { enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: queryKeys.org.councils(orgId ?? ''),
    queryFn: () => api('listCouncils', { params: { orgId: orgId! } }),
    enabled: Boolean(orgId) && enabled,
    refetchInterval: (q) =>
      q.state.data?.councils.some((c) => c.status === 'forming' || c.needsFingerprintConfirmation) ||
      (q.state.data?.setups?.length ?? 0) > 0
        ? 30_000
        : false,
  })

/** Put a fresh committee in the cached list (insert or replace). */
const storeCouncil = (qc: QueryClient, orgId: string, data: CouncilResponse) => {
  qc.setQueryData<CouncilListResponse>(queryKeys.org.councils(orgId), (prev) => {
    if (!prev) return prev
    const others = prev.councils.filter((c) => c.id !== data.council.id)
    return { ...prev, councils: [data.council, ...others] }
  })
  void qc.invalidateQueries({ queryKey: queryKeys.org.councils(orgId), exact: true })
}

/** Paste path (expert fallback): link a committee by its link or id; it then needs the fingerprint confirmation. */
export const useLinkCouncil = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CouncilLinkRequest) => api('linkCouncil', { params: { orgId }, body }),
    onSuccess: (data) => storeCouncil(qc, orgId, data),
  })
}

/** A fresh one-use pairing code (60 minutes); any earlier unused code of the organization stops working. */
export const useCreateCouncilPairing = (orgId: string, setupId?: string) =>
  useMutation({
    mutationFn: () => api('createCouncilPairing', { params: { orgId }, body: setupId ? { setupId } : {} }),
  })

/** "Create a committee": the committee shows as being set up from now on, under the chosen name. */
export const useCreateCouncilSetup = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CouncilSetupRequest) => api('createCouncilSetup', { params: { orgId }, body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.org.councils(orgId), exact: true }),
  })
}

export const useDismissCouncilSetup = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (setupId: string) => api('dismissCouncilSetup', { params: { orgId, setupId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.org.councils(orgId), exact: true }),
  })
}

export const useUpdateCouncil = (orgId: string, councilId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CouncilUpdateRequest) => api('updateCouncil', { params: { orgId, councilId }, body }),
    onSuccess: (data) => storeCouncil(qc, orgId, data),
  })
}

export const useConfirmCouncilFingerprint = (orgId: string, councilId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CouncilConfirmRequest) =>
      api('confirmCouncilFingerprint', { params: { orgId, councilId }, body }),
    onSuccess: (data) => storeCouncil(qc, orgId, data),
  })
}

export const useRefreshCouncil = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (councilId: string) => api('refreshCouncil', { params: { orgId, councilId } }),
    onSuccess: (data) => storeCouncil(qc, orgId, data),
  })
}

export const useUnlinkCouncil = (orgId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (councilId: string) => api('unlinkCouncil', { params: { orgId, councilId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.org.councils(orgId), exact: true }),
  })
}

/* ================================================================== requirements preview (R6) */

export const usePreviewRequirementsChange = (orgId: string) =>
  useMutation({
    mutationFn: (body: RequirementsChangePreviewRequest) =>
      api('previewRequirementsChange', { params: { orgId }, body }),
  })

/* ================================================================== geocoding proxy (PLAN v4 §9) */

export const useGeoSearch = () => useMutation({ mutationFn: (query: GeoSearchQuery) => api('geoSearch', { query }) })

export const useGeoBoundary = () =>
  useMutation({ mutationFn: (query: GeoBoundaryQuery) => api('geoBoundary', { query }) })
